import { isNodeV2, type NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import * as R from 'ramda'
import React, { useMemo } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { updateBlock } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  decreaseGridArea,
  moveGridArea,
  rejectNode,
  removeGridRows,
  removeGridColumns,
  addGridRows,
  addGridCols,
  increaseGridArea,
} from 'services/Store/Project/mutators/node'
import {
  getIsDragging,
  getIsEditingGrid,
  getNodeStyle,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import { safeParseArea } from '../../formControls/Area/utils'
import { GridValueType } from '../../formControls/Grid/types'
import { safeParseGrid } from '../../formControls/Grid/utils'
import { applySize } from '../../formControls/Size/utils'
import { isRoot as checkIsRoot } from '../../guards'
import { useContentPlus } from '../../useContentPlus'
import Columns from '../components/Columns'
import Rows from '../components/Rows'
import * as s from './Grid.module.scss'
import GridResizeControl from './GridResizeControl'

interface IGridMeshProps {
  node: NodeV2
  mode: IBlockMode
  block: Block
}

const isRootTemplateColumns = (columns: GridValueType) => {
  const { items } = columns
  return items.length > 2 && [R.head(items), R.last(items)].every((col) => col?.data?.unit === 'fr')
}

const GridMesh: React.FC<IGridMeshProps> = ({ node, mode, block }) => {
  const dispatch = useProjectDispatch()
  const styles = useProjectContext(getNodeStyle, block.uuid, node.id)
  const columns = useMemo(() => safeParseGrid(styles?.gridColumns), [styles?.gridColumns])
  const rows = useMemo(() => safeParseGrid(styles?.gridRows), [styles?.gridRows])
  const groupRows = Math.ceil((node?.children.length || 1) / (columns.items.length || 1))
  const isPro = mode.editorMode === AppMode.pro
  const isContentPlus = useContentPlus()
  const isRepeatable = Boolean(node?.repeatable)
  const isDragging = useProjectContext(getIsDragging)
  const isEditingGrid = useProjectContext(getIsEditingGrid, node.id)
  const isRoot = checkIsRoot(block, node)

  const hatch = isRoot && isRootTemplateColumns(columns)

  const handleDeleteRow = (index: number) => {
    if (rows.items.length <= 1) {
      return
    }
    const row = index + 1
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowStart > row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const decreaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowEnd - 1 >= row && childArea.rowStart <= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const removeChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowEnd - 1 === row && childArea.rowStart === row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['up'])) as [
        (b: Block) => Block,
      ]),
      ...(decreaseChildren?.map((child) => decreaseGridArea(child, mode.deviceMode, 'row')) as [
        (b: Block) => Block,
      ]),
      ...(removeChildren?.map((child) => rejectNode(child.id)) as [(b: Block) => Block]),
      removeGridRows(node.id, mode.deviceMode, 1, index),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  const handleDeleteColumn = (index: number) => {
    if (columns.items.length <= 1) {
      return
    }
    const column = index + 1
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colStart > column
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const decreaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colEnd - 1 >= column && childArea.colStart <= column
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const removeChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colEnd - 1 === column && childArea.colStart === column
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['left'])) as [
        (b: Block) => Block,
      ]),
      ...(decreaseChildren?.map((child) => decreaseGridArea(child, mode.deviceMode, 'column')) as [
        (b: Block) => Block,
      ]),
      ...(removeChildren?.map((child) => rejectNode(child.id)) as [(b: Block) => Block]),
      removeGridColumns(node.id, mode.deviceMode, 1, index),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  const handleAddRowTop = (index: number) => {
    const row = index + 1
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowStart >= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const increaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowEnd - 1 >= row && childArea.rowStart <= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['down'])) as [
        (b: Block) => Block,
      ]),
      ...(increaseChildren?.map((child) => increaseGridArea(child, mode.deviceMode, 'row')) as [
        (b: Block) => Block,
      ]),
      addGridRows(node.id, mode.deviceMode, 1, index, rows.items[index]?.data),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  const handleAddRowBottom = (index: number) => {
    const row = index + 2
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowStart >= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const increaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.rowEnd - 1 >= row && childArea.rowStart <= row
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['down'])) as [
        (b: Block) => Block,
      ]),
      ...(increaseChildren?.map((child) => increaseGridArea(child, mode.deviceMode, 'row')) as [
        (b: Block) => Block,
      ]),
      addGridRows(node.id, mode.deviceMode, 1, index + 1, rows.items[index]?.data),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  const handleAddColLeft = (index: number) => {
    const col = index + 1
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colStart >= col
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const increaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colEnd - 1 >= col && childArea.colStart <= col
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['right'])) as [
        (b: Block) => Block,
      ]),
      ...(increaseChildren?.map((child) => increaseGridArea(child, mode.deviceMode, 'column')) as [
        (b: Block) => Block,
      ]),
      addGridCols(node.id, mode.deviceMode, 1, index, columns.items[index]?.data),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  const handleAddColRight = (index: number) => {
    const col = index + 2
    const moveChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colStart >= col
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const increaseChildren = node.children
      .filter((id) => {
        const child = block.schema.nodes[id]
        if (!isNodeV2(child)) {
          return false
        }
        const childArea = safeParseArea(mergeNodeStyle(child.style, mode.deviceMode).gridArea)
        return childArea.colEnd - 1 >= col && childArea.colStart <= col
      })
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)

    const newBlock = R.compose(
      ...(moveChildren?.map((child) => moveGridArea(child.id, mode.deviceMode, ['right'])) as [
        (b: Block) => Block,
      ]),
      ...(increaseChildren?.map((child) => increaseGridArea(child, mode.deviceMode, 'column')) as [
        (b: Block) => Block,
      ]),
      addGridCols(node.id, mode.deviceMode, 1, index + 1, columns.items[index]?.data),
    )(block)
    dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
  }

  if (!(isPro || isContentPlus)) {
    return null
  }
  // if (isTransforming || (!isRoot && !isPro && !isDragging)) {
  //   return null
  // }

  return (
    <>
      {(!isRoot || isPro || isContentPlus || isDragging) && (
        <Columns columns={columns.items.length} gap={styles?.gridColumnsGap} nodeId={node?.id}>
          {({ index }) => (
            /* key - hotfix update portal position */
            <AbsolutePortal
              key={styles?.gridColumns}
              name={`column-${index}`}
              recalculatingKey={block}
            >
              {hatch && (index === 0 || index === columns.items.length - 1) && (
                <div className={s.hatch} />
              )}
              {(isPro || isContentPlus || isDragging) && <div className={s.column} />}
              {isEditingGrid && (
                <div className={cn(s.columnSize, { [s.active]: true })}>
                  <span className={s.sizeContent}>
                    {index + 1}
                    {isPro && `(${applySize(columns.items[index].data) || 'auto'})`}
                  </span>
                  {columns.items.length > 1 && (
                    <div className={s.controls}>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.addColLeft')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='otherAdd'
                          onClick={() => handleAddColLeft(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.removeCol')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='minusThin'
                          onClick={() => handleDeleteColumn(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.addColRight')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='otherAdd'
                          onClick={() => handleAddColRight(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}

              {isPro && isEditingGrid && !isDragging && !!index && (
                <GridResizeControl
                  block={block}
                  index={index}
                  node={node}
                  styles={styles}
                  type='col'
                />
              )}
            </AbsolutePortal>
          )}
        </Columns>
      )}
      <Rows
        gap={styles?.gridRowsGap}
        nodeId={node?.id}
        rows={isRepeatable ? groupRows : rows.items.length}
      >
        {({ index }) => {
          const size = applySize(isRepeatable ? styles?.gridAutoRows : rows.items[index].data)
          return (
            <AbsolutePortal name={`row-${index}`} recalculatingKey={block}>
              {(isPro || isContentPlus || isDragging) && <div className={s.row} />}
              {isEditingGrid && (
                <div className={cn(s.rowSize, { [s.active]: !isRepeatable })}>
                  <span className={s.sizeContent}>
                    {index + 1}
                    {isPro && size && `(${size})`}
                  </span>
                  {rows.items.length > 1 && (
                    <div className={s.controls}>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.addRowTop')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='otherAdd'
                          onClick={() => handleAddRowTop(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.removeRow')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='minusThin'
                          onClick={() => handleDeleteRow(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                      <Tooltip
                        mouseEnterDelay={0.5}
                        overlay={t('editorV2.grid.addRowBottom')}
                        placement='top'
                      >
                        <Icon
                          className={s.columnControlIcon}
                          name='otherAdd'
                          onClick={() => handleAddRowBottom(index)}
                          size={KitSize.XS}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              )}
              {isPro && isEditingGrid && !isRepeatable && !isDragging && !!index && (
                <GridResizeControl
                  block={block}
                  index={index}
                  node={node}
                  styles={styles}
                  type='row'
                />
              )}
            </AbsolutePortal>
          )
        }}
      </Rows>
    </>
  )
}

export default React.memo(GridMesh)
